import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Text, TextArea, CountrySelect } from '../components/forms';
import Image from '../components/forms/Image';
import Page from '../components/Page';
import Section from '../components/Section';
import Box from '../components/Box';
import { ButtonStyle } from '../components/Button';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { ProfileContext, Profile, ProfileManager } from '../profile';
import { useRequestAsync, APIRequest, getHeaders } from '../api/client';
import Loader from '../components/Loader';
import Flash, { ErrorFlash } from '../components/Flash';
import { CompanySelect } from '../components/forms/CompaniesSelect';

interface ProfileParams {
  company_id?: string;
  name: string;
  bio: string;
  country: string;
  city: string;
}

const barActions = (profile: Profile, push: Function) => {
  if (!profile.company_id) {
    return [];
  }

  return [
    {
      className: ButtonStyle.Confirm,
      label: 'Edit Company',
      handler: () => push('/company'),
    },
  ];
};

const updateProfile = (context: ProfileManager): [APIRequest, Function] => {
  const [state, submit] = useRequestAsync((data: ProfileParams) => ({
    url: '/users/profile',
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'X-User-ID': context.profile.id,
      'X-Token': context.profile.token,
    },
  }));

  React.useEffect(() => {
    if (!state.data) {
      return;
    }

    context.set(state.data);
  }, [state]);

  return [state, submit];
};

export default (props: RouteComponentProps<any>) => {
  const context = React.useContext(ProfileContext);
  const profile = context.profile;
  const [name, setName] = React.useState(profile.name);
  const [city, setCity] = React.useState(profile.city);
  const [country, setCountry] = React.useState(profile.country);
  const [bio, setBio] = React.useState(profile.bio);
  const [imageURL, setImageURL] = React.useState(profile.image_url);
  const [companyID, setCompanyID] = React.useState(profile.company_id);
  const [state, send] = updateProfile(context);

  const getBody = (): any => {
    const obj = {
      ...profile,
      name,
      city,
      country,
      bio,
    };

    if (profile.type === 'admin') {
      obj.company_id = companyID ? companyID : null;
    }

    return obj;
  };

  if (state.loading) {
    return <Loader />;
  }

  return (
    <Page title="Edit Profile" subtitle="Change your details">
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && <Flash text="Profile has been saved" />}
      <ActionBar
        className={ActionBarStyle.alignCenter}
        actions={barActions(profile, props.history.push)}
      />
      <form onSubmit={() => send(getBody())}>
        <Section title="Your details">
          <div className="flex">
            <div className="flex-auto w-50">
              <Text
                label="Name"
                field={['name']}
                id="name"
                defaultValue={name}
                onValueChange={(_: any, value: string) => setName(value)}
              />
              {profile.type === 'admin' ? (
                <CompanySelect
                  label="Company"
                  field={['company_id']}
                  id="company_id"
                  defaultValue={companyID}
                  onValueChange={(_: any, value: string) => setCompanyID(value)}
                />
              ) : null}
              <Text
                label="Registered Email"
                field={['email']}
                id="email"
                disabled
                defaultValue={profile.email}
                onValueChange={() => {}}
              />
              <Text
                label="City"
                field={['city']}
                id="city"
                defaultValue={city}
                onValueChange={(_: any, value: string) => setCity(value)}
              />
              <CountrySelect
                label="country"
                field={['country']}
                id="country"
                defaultValue={country}
                onValueChange={(_: any, value: string) => setCountry(value)}
              />
              <TextArea
                label="A bit about yourself"
                field={['bio']}
                id="bio"
                defaultValue={bio}
                onValueChange={(_: any, value: string) => setBio(value)}
              />
            </div>
            <div className="flex-auto pa3 ph5 tc w-50">
              <img
                src={imageURL || '/images/methods/add.png'}
                className="br-100 h5 w5 dib mb3"
                alt="Avatar"
              />
              <Image
                label="Image"
                field={['image_url']}
                id="image_url"
                defaultValue={imageURL}
                onValueChange={(_: any, value: string) => setImageURL(value)}
              />
            </div>
          </div>
        </Section>
        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.Confirm,
              label: 'Save',
              handler: () => send(getBody()),
            },
            {
              className: ButtonStyle.Danger,
              label: 'Logout',
              handler: () => {
                localStorage.removeItem('profile');
                window.location.reload();
              },
            },
          ]}
        />
      </form>
    </Page>
  );
};
